import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { ScrollToTop } from "@/components/ScrollToTop";
import { AuthProvider } from "@/contexts/AuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import Index from "./pages/Index";
import { Residences } from "./pages/Residences";
import { Amenities } from "./pages/Amenities";
import { Invest } from "./pages/Invest";
import NotFound from "./pages/NotFound";
import { PropertyDetails } from "./pages/PropertyDetails";
import { AdminLayout } from "./components/admin/AdminLayout";
import AdminLogin from "./pages/AdminLogin";
import AdminAnalytics from "./pages/AdminAnalytics";
import AdminUnits from "./pages/AdminUnits";
import AdminUnitEdit from "./pages/AdminUnitEdit";
import AdminSettings from "./pages/AdminSettings";
import AdminHubSpot from "./pages/AdminHubSpot";
import AdminMapTracer from "./pages/AdminMapTracer";
import AdminFrontFacingTracer from "./pages/AdminFrontFacingTracer";
import AdminImageManagement from "./pages/AdminImageManagement";
import AdminImageOptimizer from "./pages/AdminImageOptimizer";
import AdminGalleryImport from "./pages/AdminGalleryImport";
import { About } from "./pages/About";
import { Commercial } from "./pages/Commercial";
import { SitePlan } from "./pages/SitePlan";
import { UnitDetail } from "./pages/UnitDetail";

const queryClient = new QueryClient();

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <ScrollToTop />
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/residences" element={<Residences />} />
              <Route path="/residences/:unit-number" element={<UnitDetail />} />
              <Route path="/site-plan" element={<SitePlan />} />
              <Route path="/amenities" element={<Amenities />} />
              <Route path="/commercial" element={<Commercial />} />
              <Route path="/invest" element={<Invest />} />
              <Route path="/about" element={<About />} />
              <Route path="/property/:id" element={<PropertyDetails />} />
              
              {/* Admin Routes */}
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route 
                path="/admin" 
                element={
                  <ProtectedRoute requireManager={true}>
                    <AdminLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<AdminAnalytics />} />
                <Route path="analytics" element={<AdminAnalytics />} />
                <Route path="hubspot" element={<AdminHubSpot />} />
                <Route path="units" element={<AdminUnits />} />
                <Route path="units/:id/edit" element={<AdminUnitEdit />} />
                <Route 
                  path="map-tracer" 
                  element={
                    <ProtectedRoute requireAdmin={true}>
                      <AdminMapTracer />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="front-facing-tracer" 
                  element={
                    <ProtectedRoute requireAdmin={true}>
                      <AdminFrontFacingTracer />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="images" 
                  element={
                    <ProtectedRoute requireAdmin={true}>
                      <AdminImageManagement />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="image-optimizer" 
                  element={
                    <ProtectedRoute requireAdmin={true}>
                      <AdminImageOptimizer />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="gallery-import" 
                  element={
                    <ProtectedRoute requireAdmin={true}>
                      <AdminGalleryImport />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="settings" 
                  element={
                    <ProtectedRoute requireAdmin={true}>
                      <AdminSettings />
                    </ProtectedRoute>
                  } 
                />
              </Route>
              
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </BrowserRouter>
        </TooltipProvider>
      </AuthProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
